import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
import './AddPackagePage.css'; // Change it to match YOUR file name

function AddPackagePage() {
  const navigate = useNavigate();
  const { packageId } = useParams();
  const isEditing = Boolean(packageId);
  const { hasPermission } = useAuth();

  // State for the form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [packagedays, setPackageDays] = useState('');
  const [price, setPrice] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // This useEffect correctly fetches data when in "edit" mode.
  useEffect(() => {
    if (isEditing) {
      const fetchPackageData = async () => {
        setLoading(true);
        try {
          const docRef = doc(db, 'packages', packageId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            const data = docSnap.data();
            setName(data.name || '');
            setDescription(data.description || '');
            setPackageDays(data.packagedays || '');
            setPrice(data.price || '');
          } else {
            setError("Package not found.");
          }
        } catch (err) {
          setError("Failed to load package data.");
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchPackageData();
    }
  }, [packageId, isEditing]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Permission check
    if (isEditing && !hasPermission('packages.update')) {
      return setError("You do not have permission to update packages.");
    }
    if (!isEditing && !hasPermission('packages.create')) {
      return setError("You do not have permission to create packages.");
    }

    if (!name.trim() || !price.toString().trim() || !packagedays.trim()) {
      setError('Name, Package Duration, and Price are required.');
      return;
    }
    setLoading(true);

    const packageData = {
      name,
      description,
      packagedays,
      price: Number(price),
      updatedAt: serverTimestamp(),
    };

    try {
      if (isEditing) {
        const docRef = doc(db, 'packages', packageId);
        await updateDoc(docRef, packageData);
        alert('Package updated successfully!');
      } else {
        packageData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'packages'), packageData);
        alert('Package created successfully!');
      }
      navigate('/packages');
    } catch (err) {
      setError(`Failed to save package: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return <div className="status-message">Loading Package Details...</div>;
  }

  return (
    <div className="add-form-container">
      <h2>{isEditing ? 'Edit Package' : 'Create New Package'}</h2>
      <form onSubmit={handleSubmit} className="form">
        {error && <p className="error-message">{error}</p>}
        
        <div className="form-group">
          <label>Package Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="3" />
        </div>
        <div className="form-group">
          <label>Package Duration</label>
          <input type="text" value={packagedays} onChange={(e) => setPackageDays(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Price</label>
          <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} required />
        </div>
        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={() => navigate('/packages')} disabled={loading}>
            Cancel
          </button>
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Saving...' : (isEditing ? 'Update Package' : 'Create Package')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddPackagePage;